import { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
    Table,
    Card,
    Spin,
    Button,
    Input,
    Select,
    Row,
    Col,
    DatePicker,
    message,
    Avatar
} from "antd";
import { AuthContext } from "../context/AuthContext";
import { UserOutlined, SearchOutlined, FileExcelOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import PaySlip from "./PaySlip";

const { Option } = Select;

export default function ViewEmployees({ organisationId, organisationName, onBack }) {
    const { authData, backUrl } = useContext(AuthContext);

    const [showPayslip, setShowPayslip] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);

    const [selectedStatus, setSelectedStatus] = useState("All");

    const [pageSize, setPageSize] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [placesOfWork, setPlacesOfWork] = useState([]);

    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);
    const [selectedPlaceOfWork, setSelectedPlaceOfWork] = useState(null);
    const [selectedWorkLocation, setSelectedWorkLocation] = useState(null);

    const [searchCode, setSearchCode] = useState("");
    const [searchName, setSearchName] = useState("");
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    // Build query parameters for API calls (organisation is locked to the prop)
    const buildQueryParams = (includePagination = true, pageOverride) => {
        const params = new URLSearchParams();

        if (includePagination) {
            const activePage = pageOverride !== undefined ? pageOverride : currentPage;
            params.append("page", activePage);
            params.append("page_size", pageSize);
        }

        if (organisationId) {
            params.append("organisation", organisationId);
        }

        if (selectedStatus && selectedStatus !== "All") {
            params.append("status", selectedStatus);
        }

        if (searchCode && searchCode.trim()) {
            params.append("emp_code", searchCode.trim());
        }
        if (searchName && searchName.trim()) {
            params.append("employee_name", searchName.trim());
        }

        if (selectedDept) {
            params.append("department", selectedDept);
        }
        if (selectedDesig) {
            params.append("designation", selectedDesig);
        }
        if (selectedPlaceOfWork) {
            params.append("place_of_work", selectedPlaceOfWork);
        }
        if (selectedWorkLocation) {
            params.append("location", selectedWorkLocation);
        }

        if (fromDate) {
            params.append("from_date", dayjs(fromDate).format("YYYY-MM-DD"));
        }
        if (toDate) {
            params.append("to_date", dayjs(toDate).format("YYYY-MM-DD"));
        }

        return params.toString();
    };

    // Fetch employees with pagination
    const fetchEmployees = async (pageOverride) => {
        try {
            setLoading(true);
            const queryString = buildQueryParams(true, pageOverride);
            const url = `${backUrl}/api/emp/employees/${queryString ? `?${queryString}` : ""}`;

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            setEmployees(res.data.results || res.data);
            setTotalCount(res.data.count || res.data.length || 0);
        } catch (error) {
            console.error("Error fetching employees:", error);
            message.error("Failed to fetch employees");
        } finally {
            setLoading(false);
        }
    };

    // Fetch departments scoped to this organisation
    const fetchDepartments = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${organisationId}`);
            setDepartments(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching departments:", error);
            setDepartments([]);
        }
    };

    // Fetch designations based on department
    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`);
            setDesignations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching designations:", error);
            setDesignations([]);
        }
    };

    // Fetch places of work
    const fetchPlacesOfWork = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/worklocation/`);
            setPlacesOfWork(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching places of work:", error);
        }
    };

    // Initial load
    useEffect(() => {
        if (organisationId) {
            fetchDepartments();
            fetchPlacesOfWork();
            fetchEmployees(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organisationId]);

    // Fetch when page or page size changes
    useEffect(() => {
        if (currentPage > 1 || pageSize !== 50) {
            fetchEmployees();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize]);

    const renderValue = (value) => (value === null || value === undefined || value === "" ? "-" : value);

    const getFullName = (record) => {
        const parts = [record.salutation, record.first_name, record.surname, record.last_name]
            .filter(Boolean);
        return parts.length ? parts.join(" ") : (record.employee_name || "-");
    };

    // Excel export - simplified to match displayed columns
    const handleExportExcel = async () => {
        try {
            setExcelLoading(true);
            message.loading({ content: "Preparing Excel export...", key: "excel" });

            const queryString = buildQueryParams(false);
            const url = `${backUrl}/api/emp/employees/${queryString ? `?${queryString}` : ""}`;

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            const allEmployees = res.data.results || res.data;

            if (!allEmployees || allEmployees.length === 0) {
                message.warning({ content: "No employees to export", key: "excel" });
                return;
            }

            const XLSX = require("xlsx");
            const formatted = allEmployees.map((row) => ({
                "Employee ID": row.emp_code || row.user?.username || "-",
                "Name": getFullName(row),
                "Department": row.department_name || "-",
                "Designation": row.designation_name || "-",
            }));
            const ws = XLSX.utils.json_to_sheet(formatted);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Employees");
            XLSX.writeFile(wb, `Employees_${organisationName || ""}_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.xlsx`);

            message.success({ content: `Exported ${allEmployees.length} employees successfully!`, key: "excel" });
        } catch (error) {
            console.error("Error exporting to Excel:", error);
            message.error({ content: "Failed to export Excel file", key: "excel" });
        } finally {
            setExcelLoading(false);
        }
    };

    const handleViewPayslip = (record) => {
        setSelectedEmployee(record);
        setShowPayslip(true);
    };

    // Table columns definition - simplified
    const columns = [
        {
            title: "Employee ID",
            dataIndex: "emp_code",
            key: "emp_code",
            fixed: "left",
            width: 160,
            render: (text, record) => (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Avatar
                        size={40}
                        src={record.photo}
                        icon={!record.photo && <UserOutlined />}
                        style={{
                            backgroundColor: !record.photo ? "#1890ff" : undefined,
                            flexShrink: 0,
                        }}
                    />
                    <span>{record.emp_code || record.user?.username || text || "-"}</span>
                </div>
            ),
        },
        {
            title: "Name",
            key: "name",
            render: (_, record) => getFullName(record),
        },
        {
            title: "Department",
            dataIndex: "department_name",
            key: "department_name",
            render: renderValue,
        },
        {
            title: "Designation",
            dataIndex: "designation_name",
            key: "designation_name",
            render: renderValue,
        },
        {
            title: "Action",
            key: "action",
            fixed: "right",
            width: 160,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                    onClick={() => handleViewPayslip(record)}
                >
                    View Payslip
                </Button>
            ),
        },
    ];



    // Clear all filters and reset to initial state
    const handleClearFilters = () => {
        setSearchCode("");
        setSearchName("");
        setSelectedDept(null);
        setSelectedDesig(null);
        setSelectedPlaceOfWork(null);
        setSelectedWorkLocation(null);
        setFromDate(null);
        setToDate(null);
        setSelectedStatus("All");
        setDesignations([]);
        setCurrentPage(1);
        setPageSize(50);

        setTimeout(() => {
            fetchEmployees(1);
        }, 100);
    };

    // Apply filters
    const handleFilter = () => {
        setCurrentPage(1);
        fetchEmployees(1);
    };

    if (showPayslip && selectedEmployee) {
        return (
            <PaySlip
                employeeId={selectedEmployee.id}
                initialMonth={new Date().getMonth() + 1}
                initialYear={new Date().getFullYear()}
                onBack={() => {
                    setShowPayslip(false);
                    setSelectedEmployee(null);
                }}
            />
        );
    }

    return (
        <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                {onBack && (
                    <Button onClick={onBack}>
                        ← Back
                    </Button>
                )}
                <h3 style={{ margin: 0 }}>
                    View Employees {organisationName ? `— ${organisationName}` : ""}
                </h3>
            </div>

            <Card bordered style={{ marginBottom: 20, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                <Row style={{ marginBottom: 10 }}>
                    <Col>
                        <Button
                            type="primary"
                            icon={<FileExcelOutlined />}
                            onClick={handleExportExcel}
                            loading={excelLoading}
                            style={{ marginRight: 10, background: "green", borderColor: "green" }}
                        />
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleFilter}
                            style={{ marginRight: 10, backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                        >
                            Filter
                        </Button>
                        <Button
                            onClick={handleClearFilters}
                            style={{ backgroundColor: "#f5f5f5", color: "#000" }}
                        >
                            Clear Filters
                        </Button>
                    </Col>
                </Row>

                <Row gutter={16} style={{ marginBottom: 10 }}>
                    <Col span={6}>
                        <Input
                            allowClear
                            placeholder="Search Employee Code"
                            value={searchCode}
                            onChange={(e) => setSearchCode(e.target.value)}
                            onPressEnter={handleFilter}
                        />
                    </Col>
                    <Col span={6}>
                        <Input
                            allowClear
                            placeholder="Search Employee Name"
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            onPressEnter={handleFilter}
                        />
                    </Col>
                    <Col span={6}>
                        <Select
                            showSearch
                            allowClear
                            placeholder="Select Department"
                            style={{ width: "100%" }}
                            value={selectedDept}
                            onChange={(val) => {
                                setSelectedDept(val);
                                setSelectedDesig(null);
                                setDesignations([]);
                                if (val) fetchDesignations(val);
                            }}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {departments.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Select
                            showSearch
                            allowClear
                            placeholder="Select Designation"
                            style={{ width: "100%" }}
                            value={selectedDesig}
                            onChange={(val) => setSelectedDesig(val)}
                            disabled={!selectedDept}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {designations.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                        </Select>
                    </Col>
                </Row>

                <Row gutter={16} style={{ marginBottom: 10 }}>
                    <Col span={6}>
                        <Select
                            allowClear
                            placeholder="Select Work Location"
                            style={{ width: "100%" }}
                            value={selectedWorkLocation}
                            onChange={(val) => setSelectedWorkLocation(val)}
                        >
                            <Option value="Head Office">Head Office</Option>
                            <Option value="Site Office">Site Office</Option>
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Select
                            showSearch
                            allowClear
                            placeholder="Select Place of Work"
                            style={{ width: "100%" }}
                            value={selectedPlaceOfWork}
                            onChange={(val) => setSelectedPlaceOfWork(val)}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {placesOfWork.map((p) => <Option key={p.id} value={p.id}>{p.name}</Option>)}
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Select
                            allowClear
                            placeholder="Select Employment Status"
                            style={{ width: "100%" }}
                            value={selectedStatus}
                            onChange={setSelectedStatus}
                        >
                            <Option value="All">All</Option>
                            <Option value="Confirmed">Confirmed</Option>
                            <Option value="Probation">Probation</Option>
                            <Option value="Resigned">Resigned</Option>
                            <Option value="Notice Period">Notice Period</Option>
                            <Option value="Separated">Separated</Option>
                        </Select>
                    </Col>
                    <Col span={6}>
                        <DatePicker
                            style={{ width: "100%" }}
                            placeholder="From Date (Joining)"
                            value={fromDate}
                            onChange={(date) => setFromDate(date)}
                            allowClear
                            format="YYYY-MM-DD"
                        />
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={6}>
                        <DatePicker
                            style={{ width: "100%" }}
                            placeholder="To Date (Joining)"
                            value={toDate}
                            onChange={(date) => setToDate(date)}
                            allowClear
                            format="YYYY-MM-DD"
                        />
                    </Col>
                </Row>
            </Card>

            {loading ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Table
                    rowKey="id"
                    dataSource={employees}
                    columns={columns}
                    bordered
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalCount,
                        showSizeChanger: true,
                        pageSizeOptions: ["8", "10", "20", "50", "100"],
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        },
                        onShowSizeChange: (_, size) => {
                            setPageSize(size);
                            setCurrentPage(1);
                        },
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}
        </>
    );
}